import React, { useState, useEffect, useContext } from "react";
import styled, { keyframes } from "styled-components";
import { CopilotContext } from "../../ProtoType/CopilotV2/CopilotContext";

// Guide option definitions with descriptions
const GUIDE_OPTIONS = [
  {
    id: "question-coach",
    title: "Question Coach",
    description: "Get suggestions on how to improve your existing questions for better quality responses",
    icon: "💬",
    prompt: (survey) => `You are a survey design expert. Analyze the following survey questions and provide specific improvements with clear rationales for each. Focus on clarity, bias reduction, and response quality:

QUESTIONS:
${Array.isArray(survey.items) ? survey.items.map((item, index) => {
  const question = item?.properties?.question || "Untitled Question";
  return `${index + 1}. ${JSON.stringify(question)}`;
}).join("\n") : "No questions available"}

Provide 3-5 specific suggestions. For each suggestion:
1. Identify the specific question
2. Explain what can be improved
3. Provide a concrete rewrite example
4. Explain why this improvement will lead to better data

Format your response as JSON: { "suggestions": [{ "originalQuestion": "", "issue": "", "improvement": "", "rationale": "" }] }`
  },
  {
    id: "new-questions",
    title: "New Questions",
    description: "Get ideas for additional questions that would complement your survey",
    icon: "✨",
    prompt: (survey) => `You are a survey design expert. Based on the following survey, suggest new questions that would enhance the data collection. Consider what might be missing from the current survey.

SURVEY TITLE: ${survey?.title || "Untitled Survey"}
DESCRIPTION: ${survey?.description || ""}
CURRENT QUESTIONS:
${Array.isArray(survey.items) ? survey.items.map((item, index) => {
  const question = item?.properties?.question || "Untitled Question";
  return `${index + 1}. ${JSON.stringify(question)}`;
}).join("\n") : "No questions available"}

Provide 3-5 new questions that would complement this survey. For each suggestion:
1. Provide the question text
2. Suggest an appropriate question type (Likert, Multiple Choice, Text, etc.)
3. Explain why this question would be valuable to include

Format your response as JSON: { "suggestions": [{ "questionText": "", "questionType": "", "rationale": "" }] }`
  }
];

const AISurveyGuideModal = ({ isOpen, onClose, surveyData }) => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState("select"); // "select", "loading", or "suggestions"
  // Track which suggestions have been applied
  const [appliedSuggestions, setAppliedSuggestions] = useState({});

  // Get the OpenAI API function from context
  const copilotContext = useContext(CopilotContext);
  
  // Check if context or required functions are available
  const hasApiSupport = copilotContext && typeof copilotContext.callOpenAIJSON === 'function';

  // Check if there are questions in the survey
  const hasQuestions = Array.isArray(surveyData?.items) && surveyData.items.length > 0;

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleClose = () => {
    setSelectedOption(null);
    setSuggestions([]);
    setStep("select");
    onClose();
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
  };

  const handleNext = async () => {
    if (!selectedOption) return;
    
    setStep("loading");
    setIsLoading(true);
    
    try {
      // Check if API support is available
      if (!hasApiSupport) {
        throw new Error("API support is not available. The CopilotContext may not be properly configured.");
      }
      
      // Get the prompt based on the selected option
      const prompt = selectedOption.prompt(surveyData);
      
      // Call OpenAI
      const response = await copilotContext.callOpenAIJSON(prompt);
      
      // Parse the JSON response
      const parsedResponse = JSON.parse(response);
      
      // Set suggestions
      setSuggestions(parsedResponse.suggestions || []);
      
      // Move to suggestions step
      setStep("suggestions");
    } catch (error) {
      console.error("Error getting suggestions:", error);
      // Handle error state
      setSuggestions([{
        error: true,
        message: error.message || "Failed to generate suggestions. Please try again."
      }]);
      setStep("suggestions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplySuggestion = (suggestion, index) => {
    try {
      // This will be implemented based on the suggestion type
      if (selectedOption.id === "question-coach") {
        // Improve existing question
        if (!suggestion?.originalQuestion || !suggestion?.improvement) {
          console.error("Missing required data for question improvement");
          return;
        }
        
        const questionToImprove = surveyData?.items?.find(
          item => item?.properties?.question === suggestion.originalQuestion
        );
        
        if (questionToImprove && surveyData?.updateQuestion) {
          // Call the update function from parent component
          surveyData.updateQuestion(questionToImprove.uniqueId, {
            ...questionToImprove.properties,
            question: suggestion.improvement
          });
          
          // Mark this suggestion as applied
          setAppliedSuggestions(prev => ({
            ...prev,
            [`${selectedOption.id}-${index}`]: true
          }));
        } else {
          console.error("Cannot find question to improve or updateQuestion function not available");
        }
      } else if (selectedOption.id === "new-questions") {
        // Add new question
        if (!suggestion?.questionText || !suggestion?.questionType) {
          console.error("Missing required data for new question");
          return;
        }
        
        if (surveyData?.addQuestion) {
          surveyData.addQuestion({
            type: mapQuestionTypeToComponentType(suggestion.questionType),
            properties: {
              question: suggestion.questionText
            }
          });
          
          // Mark this suggestion as applied
          setAppliedSuggestions(prev => ({
            ...prev,
            [`${selectedOption.id}-${index}`]: true
          }));
        } else {
          console.error("addQuestion function not available");
        }
      }
    } catch (error) {
      console.error("Error applying suggestion:", error);
    }
  };

  // Helper function to map question types
  const mapQuestionTypeToComponentType = (questionType) => {
    const typeMap = {
      "Likert": "likert",
      "Multiple Choice": "radio",
      "Checkbox": "checkbox",
      "Text": "text",
      "Dropdown": "dropdown",
      "Slider": "slider"
    };
    
    return typeMap[questionType] || "text";
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>AI Survey Guide</ModalTitle>
          <CloseButton onClick={handleClose}>&times;</CloseButton>
        </ModalHeader>
        
        {step === "select" && (
          <>
            <ModalBody>
              <IntroText>
                Select an AI assistant to help improve your survey. 
                The AI will analyze your current survey design and provide targeted suggestions.
              </IntroText>
              
              {!hasApiSupport && (
                <WarningMessage>
                  Warning: AI API support is not available. The CopilotContext may not be properly configured. 
                  AI suggestions may not work correctly.
                </WarningMessage>
              )}
              
              <OptionsContainer>
                {GUIDE_OPTIONS.map((option) => {
                  // Disable question coach if there are no questions
                  const isDisabled = option.id === "question-coach" && !hasQuestions;
                  
                  return (
                    <OptionCard 
                      key={option.id}
                      selected={selectedOption?.id === option.id}
                      onClick={() => !isDisabled && handleOptionSelect(option)}
                      disabled={isDisabled}
                    >
                      <OptionIcon>{option.icon}</OptionIcon>
                      <OptionTitle>{option.title}</OptionTitle>
                      <OptionDescription>
                        {isDisabled 
                          ? "You need to add questions to your survey first"
                          : option.description
                        }
                      </OptionDescription>
                    </OptionCard>
                  );
                })}
              </OptionsContainer>
            </ModalBody>
            
            <ModalFooter>
              <CancelButton onClick={handleClose}>Cancel</CancelButton>
              <NextButton 
                onClick={handleNext} 
                disabled={!selectedOption}
              >
                Next
              </NextButton>
            </ModalFooter>
          </>
        )}
        
        {step === "loading" && (
          <LoadingContainer>
            <Spinner />
            <LoadingText>Analyzing your survey...</LoadingText>
          </LoadingContainer>
        )}
        
        {step === "suggestions" && (
          <>
            <ModalBody>
              <SectionTitle>{selectedOption.title} Suggestions</SectionTitle>
              
              <SuggestionsContainer>
                {suggestions.map((suggestion, index) => {
                  // Determine if this suggestion has been applied
                  const isApplied = appliedSuggestions[`${selectedOption.id}-${index}`];
                  
                  return (
                    <SuggestionCard key={index} applied={isApplied}>
                      {isApplied && <AppliedCheckmark>✓</AppliedCheckmark>}
                      
                      {suggestion.error ? (
                        <ErrorMessage>{suggestion.message}</ErrorMessage>
                      ) : selectedOption.id === "question-coach" ? (
                        <>
                          <SuggestionOriginal>Original: {suggestion.originalQuestion}</SuggestionOriginal>
                          <SuggestionImprovement>Improved: {suggestion.improvement}</SuggestionImprovement>
                          <SuggestionRationale>Why: {suggestion.rationale}</SuggestionRationale>
                          {!isApplied && (
                            <ApplyButton 
                              onClick={() => handleApplySuggestion(suggestion, index)}
                            >
                              Apply Change
                            </ApplyButton>
                          )}
                        </>
                      ) : selectedOption.id === "new-questions" ? (
                        <>
                          <SuggestionTitle>New Question</SuggestionTitle>
                          <SuggestionText>{suggestion.questionText}</SuggestionText>
                          <SuggestionDetail>Type: {suggestion.questionType}</SuggestionDetail>
                          <SuggestionRationale>Why: {suggestion.rationale}</SuggestionRationale>
                          {!isApplied && (
                            <ApplyButton 
                              onClick={() => handleApplySuggestion(suggestion, index)}
                            >
                              Add to Survey
                            </ApplyButton>
                          )}
                        </>
                      ) : (
                        <>
                          <SuggestionTitle>Suggestion: {suggestion.action}</SuggestionTitle>
                          <SuggestionText>{suggestion.details}</SuggestionText>
                          <SuggestionRationale>Why: {suggestion.rationale}</SuggestionRationale>
                          {!isApplied && (
                            <ApplyButton 
                              onClick={() => handleApplySuggestion(suggestion, index)}
                            >
                              Apply Suggestion
                            </ApplyButton>
                          )}
                        </>
                      )}
                    </SuggestionCard>
                  );
                })}
              </SuggestionsContainer>
            </ModalBody>
            
            <ModalFooter>
              <CancelButton onClick={handleClose}>Close</CancelButton>
              <NextButton onClick={() => {
                setStep("select");
                setAppliedSuggestions({});
              }}>
                Try Another Guide
              </NextButton>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

// Styled components
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-in-out;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 80%;
  max-width: 800px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s ease-in-out;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-family: "Raleway", sans-serif;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #4a90e2;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #888;
  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  max-height: calc(80vh - 140px);
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 15px 20px;
  border-top: 1px solid #eee;
`;

const IntroText = styled.p`
  margin-bottom: 20px;
  font-size: 1rem;
  color: #555;
  line-height: 1.5;
`;

const OptionsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div`
  background: ${props => {
    if (props.disabled) return '#f5f5f5';
    return props.selected ? '#ebf5ff' : '#fff';
  }};
  border: 2px solid ${props => {
    if (props.disabled) return '#cccccc';
    return props.selected ? '#4a90e2' : '#e0e0e0';
  }};
  border-radius: 8px;
  padding: 20px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  opacity: ${props => props.disabled ? 0.7 : 1};
  
  &:hover {
    border-color: ${props => props.disabled ? '#cccccc' : '#4a90e2'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
    box-shadow: ${props => props.disabled ? 'none' : '0 5px 15px rgba(0, 0, 0, 0.08)'};
  }
`;

const OptionIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 10px;
`;

const OptionTitle = styled.h3`
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  font-weight: 600;
`;

const OptionDescription = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #666;
  line-height: 1.4;
`;

const CancelButton = styled.button`
  background: #f5f5f5;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  margin-right: 10px;
  
  &:hover {
    background: #e0e0e0;
  }
`;

const NextButton = styled.button`
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover:not(:disabled) {
    background: #3a7bc8;
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4a90e2;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 20px;
`;

const LoadingText = styled.p`
  font-size: 1rem;
  color: #666;
`;

const SectionTitle = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.3rem;
  color: #4a90e2;
`;

const SuggestionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SuggestionCard = styled.div`
  background: ${props => props.applied ? '#f8fff9' : '#fff'};
  border: 1px solid ${props => props.applied ? '#a0d9b4' : '#e0e0e0'};
  border-radius: 6px;
  padding: 16px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
`;

const SuggestionTitle = styled.h4`
  margin: 0 0 10px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
`;

const SuggestionText = styled.p`
  margin: 0 0 12px 0;
  font-size: 1rem;
  color: #333;
  line-height: 1.5;
`;

const SuggestionDetail = styled.p`
  margin: 8px 0;
  font-size: 0.9rem;
  color: #666;
`;

const SuggestionOriginal = styled.p`
  margin: 0 0 8px 0;
  font-size: 0.9rem;
  color: #666;
  text-decoration: line-through;
`;

const SuggestionImprovement = styled.p`
  margin: 0 0 12px 0;
  font-size: 1rem;
  color: #22c55e;
  font-weight: 500;
`;

const SuggestionRationale = styled.p`
  margin: 8px 0 16px 0;
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
  line-height: 1.4;
  border-left: 3px solid #4a90e2;
  padding-left: 10px;
`;

const ApplyButton = styled.button`
  background: ${props => props.disabled ? '#a0d9b4' : '#22c55e'};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 0.9rem;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  float: right;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${props => props.disabled ? '#a0d9b4' : '#16a34a'};
  }
`;

const ErrorMessage = styled.p`
  color: #ef4444;
  font-size: 1rem;
  text-align: center;
  margin: 20px 0;
`;

const WarningMessage = styled.p`
  color: #ef4444;
  font-size: 1rem;
  text-align: center;
  margin: 20px 0;
`;

const AppliedCheckmark = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: #22c55e;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export default AISurveyGuideModal;